import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Phone, IdentificationBadge, X, UploadSimple, FileCsv } from "@phosphor-icons/react";

const EMPTY = { name: "", phone: "", badge_number: "", notes: "" };

// Minimal CSV parser: handles quoted values, commas inside quotes, escaped quotes
const parseCsv = (text) => {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim());
  if (lines.length === 0) return { header: [], rows: [] };
  const parseLine = (line) => {
    const out = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQuote) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQuote = false; }
        else cur += c;
      } else if (c === '"') { inQuote = true; }
      else if (c === ",") { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const header = parseLine(lines[0]).map((h) => h.toLowerCase().replace(/[^a-z0-9_]/g, "_"));
  const rows = lines.slice(1).map(parseLine);
  return { header, rows };
};

const headerAliases = {
  name: ["name", "full_name", "guard", "guard_name"],
  phone: ["phone", "phone_number", "mobile", "sms", "sms_number", "cell"],
  badge_number: ["badge", "badge_number", "badge_no", "id"],
  notes: ["notes", "note", "comment", "comments"],
};

const mapRowToGuard = (header, row) => {
  const obj = {};
  header.forEach((h, i) => { obj[h] = row[i] ?? ""; });
  const pick = (key) => {
    for (const a of headerAliases[key]) if (obj[a]) return obj[a];
    return "";
  };
  return {
    name: pick("name"),
    phone: pick("phone"),
    badge_number: pick("badge_number") || undefined,
    notes: pick("notes") || undefined,
  };
};

const Guards = () => {
  const [guards, setGuards] = useState([]);
  const [stats, setStats] = useState({});  // guard_id -> stats
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [g, s] = await Promise.all([
        api.get("/guards"),
        api.get("/guards/stats?days=30"),
      ]);
      setGuards(g.data);
      const map = {};
      s.data.forEach((row) => { map[row.guard_id] = row; });
      setStats(map);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ name: g.name, phone: g.phone, badge_number: g.badge_number || "", notes: g.notes || "" }); setOpen(true); };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { header, rows } = parseCsv(String(reader.result || ""));
        if (header.length === 0) { toast.error("Empty CSV"); return; }
        const mapped = rows.map((r) => mapRowToGuard(header, r));
        const valid = mapped.filter((m) => m.name && m.phone);
        if (valid.length === 0) {
          toast.error("No valid rows. Make sure CSV has 'name' and 'phone' columns.");
          return;
        }
        setImportRows(valid);
        setImportOpen(true);
      } catch (err) {
        toast.error("Failed to parse CSV");
      }
    };
    reader.readAsText(file);
    e.target.value = "";  // reset so picking same file again re-fires
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/guards/import", { rows: importRows });
      toast.success(`Imported ${data.imported} guards (${data.skipped} duplicates skipped${data.errors.length ? `, ${data.errors.length} errors` : ""})`);
      setImportOpen(false);
      setImportRows([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/guards/${editing.guard_id}`, form);
        toast.success("Guard updated");
      } else {
        await api.post("/guards", form);
        toast.success("Guard added to roster");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Remove ${g.name} from the roster?`)) return;
    try {
      await api.delete(`/guards/${g.guard_id}`);
      toast.success("Guard removed");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="guards-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">// PERSONNEL</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Guard Roster</h1>
          <p className="text-sm text-zinc-500 mt-1">All field personnel that Sandy will text for shift check-in.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="csv-file-input"
            className="hidden"
            onChange={handleFile}
          />
          <button
            data-testid="import-csv-btn"
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 border border-[#2BD4D9]/40 text-[#2BD4D9] hover:bg-[#2BD4D9]/10 px-4 py-2.5 text-xs uppercase tracking-wider"
          >
            <UploadSimple size={14} weight="bold" /> Import CSV
          </button>
          <button
            data-testid="add-guard-btn"
            onClick={openCreate}
            className="flex items-center gap-2 bg-[#007AFF] hover:bg-[#0066d6] px-4 py-2.5 text-xs uppercase tracking-wider font-semibold"
          >
            <Plus size={14} weight="bold" /> Add Guard
          </button>
        </div>
      </div>

      <div className="tac-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 label-mono">LOADING ROSTER...</div>
        ) : guards.length === 0 ? (
          <div className="p-12 text-center">
            <div className="label-mono mb-2">// ROSTER EMPTY</div>
            <p className="text-sm text-zinc-400">Add your first guard to start dispatching.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3 label-mono">Guard</th>
                <th className="text-left px-4 py-3 label-mono">Badge</th>
                <th className="text-left px-4 py-3 label-mono">Phone</th>
                <th className="text-left px-4 py-3 label-mono">On-Time (30d)</th>
                <th className="text-right px-4 py-3 label-mono">Actions</th>
              </tr>
            </thead>
            <tbody>
              {guards.map((g) => {
                const s = stats[g.guard_id];
                const tot = s?.total_shifts ?? 0;
                const pct = s?.on_time_pct ?? null;
                const color =
                  pct === null || tot === 0 ? "text-zinc-500" :
                  pct >= 95 ? "text-emerald-400" :
                  pct >= 80 ? "text-yellow-400" :
                  "text-red-400";
                return (
                <tr key={g.guard_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`guard-row-${g.guard_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 grid place-items-center bg-white/5 border border-white/10 font-mono text-xs uppercase">
                        {g.name.split(" ").map(n => n[0]).join("").slice(0,2)}
                      </div>
                      <div>
                        <div className="font-medium">{g.name}</div>
                        {g.notes && <div className="text-xs text-zinc-500 truncate max-w-xs">{g.notes}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-300">{g.badge_number || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-300">{g.phone}</td>
                  <td className="px-4 py-3" data-testid={`reliability-${g.guard_id}`}>
                    {tot === 0 ? (
                      <span className="text-xs text-zinc-600 font-mono">no data</span>
                    ) : (
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={`font-mono font-bold text-lg ${color}`}>{pct}%</span>
                        <span className="text-zinc-700">·</span>
                        <span className="text-[0.65rem] text-zinc-500 font-mono">{s.arrived + s.completed}/{tot}</span>
                        {s.no_show > 0 && <span className="text-[0.65rem] text-red-400 font-mono">{s.no_show} no-show</span>}
                        {s.late > 0 && <span className="text-[0.65rem] text-yellow-400 font-mono">{s.late} late</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button data-testid={`edit-guard-${g.guard_id}`} onClick={() => openEdit(g)} className="border border-white/15 hover:bg-white/5 p-2"><PencilSimple size={12}/></button>
                      <button data-testid={`delete-guard-${g.guard_id}`} onClick={() => remove(g)} className="border border-white/15 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-400 p-2"><Trash size={12}/></button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="guard-modal">
          <div className="bg-[#121212] border border-white/10 max-w-md w-full">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
              <div className="label-mono">{editing ? "// EDIT GUARD" : "// ADD GUARD"}</div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-3">
              <Field icon={IdentificationBadge} label="Full Name" required value={form.name} onChange={(v)=>setForm({...form, name:v})} testid="guard-name" />
              <Field icon={Phone} label="SMS Number (E.164 format)" required value={form.phone} onChange={(v)=>setForm({...form, phone:v})} placeholder="+15555550123" testid="guard-phone" />
              <Field icon={IdentificationBadge} label="Badge Number" value={form.badge_number} onChange={(v)=>setForm({...form, badge_number:v})} testid="guard-badge" />
              <div>
                <label className="label-mono block mb-1">Notes</label>
                <textarea
                  data-testid="guard-notes"
                  value={form.notes}
                  onChange={(e)=>setForm({...form, notes: e.target.value})}
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={()=>setOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Cancel</button>
                <button data-testid="save-guard-btn" type="submit" className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">
                  {editing ? "Update" : "Add to Roster"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="import-modal">
          <div className="bg-[#121212] border border-white/10 max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
              <div className="flex items-center gap-2">
                <FileCsv size={16} className="text-[#2BD4D9]"/>
                <div className="label-mono">// IMPORT PREVIEW · {importRows.length} ROWS</div>
              </div>
              <button onClick={() => setImportOpen(false)} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>
            <div className="px-5 py-3 text-xs text-zinc-400 border-b border-white/5">
              Sandy will skip any rows whose phone number already exists in the roster.
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="text-zinc-500 border-b border-white/10 sticky top-0 bg-[#121212]">
                  <tr>
                    <th className="text-left px-4 py-2 label-mono">Name</th>
                    <th className="text-left px-4 py-2 label-mono">Phone</th>
                    <th className="text-left px-4 py-2 label-mono">Badge</th>
                    <th className="text-left px-4 py-2 label-mono">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((r, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="px-4 py-2">{r.name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.phone}</td>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-400">{r.badge_number || "—"}</td>
                      <td className="px-4 py-2 text-xs text-zinc-400 truncate max-w-xs">{r.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-white/10">
              <button type="button" onClick={() => setImportOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Cancel</button>
              <button
                data-testid="confirm-import-btn"
                onClick={confirmImport}
                disabled={importing}
                className="px-4 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
              >
                {importing ? "Importing..." : `Import ${importRows.length} Guards`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, placeholder, required, testid }) => (
  <div>
    <label className="label-mono block mb-1">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={14} />}
      <input
        data-testid={testid}
        required={required}
        value={value}
        onChange={(e)=>onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none ${Icon?"pl-9":"pl-3"} pr-3 py-2 text-sm`}
      />
    </div>
  </div>
);

export default Guards;
